import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSidebar } from '../components/AppSidebar';
import { AppToastViewport } from '../components/AppToastViewport';
import { AppTopBar } from '../components/AppTopBar';
import { AiChatPanel } from '../features/chat/components/AiChatPanel';
import { ChatHeader } from '../features/chat/components/ChatHeader';
import { BookmarkPlus, PanelLeft, Share2 } from 'lucide-react';
import { ChatShareDialog } from '../features/assistant/ChatShareDialog';
import { SkillDraftDialog } from '../features/assistant/SkillDraftDialog';
import type { AiVisibility } from '../features/assistant/AiShareFields';
import { fetchMe } from '../lib/meClient';
import { ChatHistorySidebar, type AiChatHistoryItem, type ChatPinPatch } from '../features/chat/components/ChatHistorySidebar';
import { ChatSearchView } from '../features/chat/components/ChatSearchView';
import { ChatProjectsView } from '../features/chat/components/ChatWorkspacePanels';
import { ChatCustomizeView } from '../features/chat/components/ChatCustomizeView';
import { AssistantSettingsSection } from '../features/settings/sections/AssistantSettingsSection';
import { deleteAiChat, pinAiChat, renameAiChat } from '../features/chat/utils/chatApi';
import { applyPinnedAt } from '../features/chat/utils/groupAiChats';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useToasts } from '../hooks/useToasts';
import { chatPath } from '../utils/dashboardRoutes';

export function ChatPage() {
  const { instanceId, chatId: urlChatId } = useParams<{ instanceId: string; chatId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const chatView = searchParams.get('view');
  const isSearchActive = chatView === 'search';
  const workspaceView = chatView === 'projects' || chatView === 'customize' || chatView === 'scheduled'
    ? chatView
    : null;
  const activeProjectId = searchParams.get('project');
  const chatId = urlChatId ?? null;

  const [chatKey, setChatKey] = useState<string>(() => urlChatId ?? '__new__');
  const isNewChatSession = chatKey.startsWith('__new__');
  const panelChatId = isNewChatSession ? null : chatId;
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  const [recentChatsList, setRecentChatsList] = useState<AiChatHistoryItem[]>([]);
  const [currentChatTitle, setCurrentChatTitle] = useState<string | null>(null);
  const [currentChatPinnedAt, setCurrentChatPinnedAt] = useState<string | null>(null);
  const [chatAccess, setChatAccess] = useState<'owner' | 'read'>('owner');
  const [chatVisibility, setChatVisibility] = useState<AiVisibility>('private');
  const [chatGrantees, setChatGrantees] = useState<string[]>([]);
  const [skillDraftOpen, setSkillDraftOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [canEnableSkill, setCanEnableSkill] = useState(false);
  const [pinPatch, setPinPatch] = useState<ChatPinPatch | null>(null);
  const skipNextUrlSyncRef = useRef(false);
  const activeChatIdRef = useRef<string | null>(chatId);
  const pinTokenRef = useRef(0);
  const [chatSidebarPinned, setChatSidebarPinned] = useState(true);
  const [chatSidebarHover, setChatSidebarHover] = useState(false);
  const chatSidebarCloseTimerRef = useRef<number | null>(null);
  const chatSidebarSuppressHoverRef = useRef(false);
  const chatSidebarPinnedRef = useRef(true);
  const chatSidebarVisible = chatSidebarPinned || chatSidebarHover;
  const { toasts, pushToast, dismissToast } = useToasts();

  useDocumentTitle(
    isSearchActive ? 'Chats durchsuchen'
      : workspaceView === 'projects' ? 'Projekte'
        : workspaceView === 'customize' ? 'Anpassungen'
          : workspaceView === 'scheduled' ? 'Geplant'
            : currentChatTitle,
  );

  useEffect(() => {
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    setChatKey((current) => {
      if (urlChatId) return current === urlChatId ? current : urlChatId;
      return current.startsWith('__new__') ? current : `__new__${Date.now()}`;
    });
  }, [urlChatId]);

  useEffect(() => {
    activeChatIdRef.current = chatId;
    setCurrentChatTitle(null);
    setCurrentChatPinnedAt(null);
    setChatAccess('owner');
    setChatVisibility('private');
    setChatGrantees([]);
    setSkillDraftOpen(false);
    setShareOpen(false);
  }, [chatId]);

  useEffect(() => {
    return () => {
      if (chatSidebarCloseTimerRef.current != null) window.clearTimeout(chatSidebarCloseTimerRef.current);
    };
  }, []);

  const cancelChatSidebarClose = useCallback(() => {
    if (chatSidebarCloseTimerRef.current != null) {
      window.clearTimeout(chatSidebarCloseTimerRef.current);
      chatSidebarCloseTimerRef.current = null;
    }
  }, []);

  const suppressChatSidebarHover = useCallback(() => {
    chatSidebarSuppressHoverRef.current = true;
    window.setTimeout(() => {
      chatSidebarSuppressHoverRef.current = false;
    }, 800);
  }, []);

  const beginChatSidebarHover = useCallback(() => {
    if (chatSidebarSuppressHoverRef.current) return;
    cancelChatSidebarClose();
    setChatSidebarHover(true);
  }, [cancelChatSidebarClose]);

  const endChatSidebarHover = useCallback(() => {
    cancelChatSidebarClose();
    chatSidebarCloseTimerRef.current = window.setTimeout(() => {
      setChatSidebarHover(false);
      chatSidebarCloseTimerRef.current = null;
    }, 200);
  }, [cancelChatSidebarClose]);

  const collapseChatSidebar = useCallback(() => {
    cancelChatSidebarClose();
    suppressChatSidebarHover();
    chatSidebarPinnedRef.current = false;
    setChatSidebarPinned(false);
    setChatSidebarHover(false);
  }, [cancelChatSidebarClose, suppressChatSidebarHover]);

  const toggleChatSidebarPin = useCallback(() => {
    cancelChatSidebarClose();
    if (chatSidebarPinnedRef.current) {
      collapseChatSidebar();
      return;
    }
    chatSidebarPinnedRef.current = true;
    setChatSidebarPinned(true);
    setChatSidebarHover(false);
  }, [cancelChatSidebarClose, collapseChatSidebar]);

  const projectSearch = activeProjectId ? `?project=${encodeURIComponent(activeProjectId)}` : '';

  const handleOpenSearch = useCallback(() => {
    if (!instanceId) return;
    const next = new URLSearchParams(searchParams);
    next.set('view', 'search');
    setSearchParams(next);
  }, [instanceId, searchParams, setSearchParams]);

  const handleOpenView = useCallback((view: 'projects' | 'customize' | 'scheduled' | null) => {
    const next = new URLSearchParams(searchParams);
    if (view) next.set('view', view);
    else next.delete('view');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handleOpenProject = useCallback((nextProjectId: string | null) => {
    if (!instanceId) return;
    if (nextProjectId) {
      setChatKey(`__new__${Date.now()}`);
      navigate(`${chatPath(instanceId)}?project=${encodeURIComponent(nextProjectId)}`);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.delete('project');
    setSearchParams(next);
  }, [instanceId, navigate, searchParams, setSearchParams]);

  const handleNewChat = useCallback(() => {
    if (!instanceId) return;
    setChatKey(`__new__${Date.now()}`);
    navigate(`${chatPath(instanceId)}${projectSearch}`);
  }, [instanceId, navigate, projectSearch]);

  const handleSelectChat = useCallback((id: string) => {
    if (!instanceId) return;
    navigate(`${chatPath(instanceId, id)}${projectSearch}`);
  }, [instanceId, navigate, projectSearch]);

  const handleChatCreated = useCallback((id: string) => {
    if (!instanceId) return;
    setSidebarRefreshKey((k) => k + 1);
    skipNextUrlSyncRef.current = true;
    navigate(`${chatPath(instanceId, id)}${projectSearch}`, { replace: true });
  }, [instanceId, navigate, projectSearch]);

  const handleTitleGenerated = useCallback((generatedChatId: string, title: string) => {
    if (generatedChatId === activeChatIdRef.current) {
      setCurrentChatTitle(title);
    }
    setSidebarRefreshKey((k) => k + 1);
  }, []);

  const handleChatRenamed = useCallback((id: string, title: string) => {
    if (id === chatId) setCurrentChatTitle(title);
  }, [chatId]);

  const handleChatMetadata = useCallback((chat: {
    title: string;
    pinned_at: string | null;
    access?: 'owner' | 'read';
    visibility?: AiVisibility;
    grantee_user_ids?: string[];
  }) => {
    setCurrentChatTitle(chat.title);
    setCurrentChatPinnedAt(chat.pinned_at);
    setChatAccess(chat.access ?? 'owner');
    setChatVisibility(chat.visibility ?? 'private');
    setChatGrantees(chat.grantee_user_ids ?? []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled) return;
      setCanEnableSkill(me?.role === 'owner' || me?.role === 'admin');
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const applyPinLocally = useCallback((targetId: string, pinnedAt: string | null) => {
    setRecentChatsList((prev) => applyPinnedAt(prev, targetId, pinnedAt));
    pinTokenRef.current += 1;
    setPinPatch({ chatId: targetId, pinnedAt, token: pinTokenRef.current });
    if (targetId === activeChatIdRef.current) setCurrentChatPinnedAt(pinnedAt);
  }, []);

  const handleTogglePin = useCallback(async (
    targetId: string,
    pinned: boolean,
    previousPinnedAt: string | null,
  ) => {
    const optimistic = pinned ? (previousPinnedAt ?? new Date().toISOString()) : null;
    applyPinLocally(targetId, optimistic);
    try {
      const serverPinnedAt = await pinAiChat(targetId, pinned);
      applyPinLocally(targetId, serverPinnedAt);
    } catch (err) {
      applyPinLocally(targetId, previousPinnedAt);
      pushToast(err instanceof Error ? err.message : 'Anheften konnte nicht gespeichert werden.', 'error');
    }
  }, [applyPinLocally, pushToast]);

  const handleHeaderRename = useCallback(async (nextTitle: string) => {
    if (!chatId) return;
    const previousTitle = currentChatTitle;
    setCurrentChatTitle(nextTitle); // optimistisch
    try {
      const savedTitle = await renameAiChat(chatId, nextTitle);
      setCurrentChatTitle(savedTitle);
      setSidebarRefreshKey((key) => key + 1);
    } catch (err) {
      setCurrentChatTitle(previousTitle); // Rollback
      pushToast(err instanceof Error ? err.message : 'Titel konnte nicht gespeichert werden.', 'error');
    }
  }, [chatId, currentChatTitle, pushToast]);

  const handleDeleteChat = useCallback(async (targetId: string) => {
    try {
      await deleteAiChat(targetId);
      setSidebarRefreshKey((key) => key + 1);
      setRecentChatsList((prev) => prev.filter((chat) => chat.id !== targetId));
      if (targetId === activeChatIdRef.current) handleNewChat();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Chat konnte nicht gelöscht werden.', 'error');
    }
  }, [handleNewChat, pushToast]);

  const handleHeaderDelete = useCallback(async () => {
    if (!chatId) return;
    await handleDeleteChat(chatId);
  }, [chatId, handleDeleteChat]);

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar active="chat" />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="KI Chat" />
        <div
          className="flex flex-1 min-h-0 overflow-hidden transition-[--chat-sidebar-width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
          style={{ ['--chat-sidebar-width' as string]: chatSidebarVisible ? '260px' : '0px' }}
        >
          <ChatHistorySidebar
            currentChatId={isSearchActive || workspaceView ? null : chatId}
            onSelect={handleSelectChat}
            onNew={handleNewChat}
            onOpenSearch={handleOpenSearch}
            isSearchingActive={isSearchActive}
            refreshKey={sidebarRefreshKey}
            onChatRenamed={handleChatRenamed}
            onChatsLoaded={setRecentChatsList}
            onTogglePin={handleTogglePin}
            pinPatch={pinPatch}
            activeView={workspaceView}
            onOpenView={handleOpenView}
            projectId={activeProjectId}
            onClearProject={() => handleOpenProject(null)}
            onCollapse={toggleChatSidebarPin}
            onHoverStart={beginChatSidebarHover}
            onHoverEnd={endChatSidebarHover}
            collapseLabel={chatSidebarPinned ? 'Seitenleiste ausblenden' : 'Seitenleiste offen lassen'}
            open={chatSidebarVisible}
          />
          <section className="relative flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden bg-app-surface-muted">
            {/* Header und Nachrichtenbereich teilen sich Fläche und Verlauf, damit dazwischen
                keine sichtbare Kante entsteht. */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.14),transparent_50%)]" />
            <button
              type="button"
              onMouseEnter={beginChatSidebarHover}
              onMouseLeave={endChatSidebarHover}
              onClick={toggleChatSidebarPin}
              className={`absolute left-2 top-0 z-40 hidden h-10 w-10 items-center justify-center rounded-md text-app-text-subtle transition-opacity duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-app-hover hover:text-app-text-muted motion-reduce:transition-none md:inline-flex ${
                chatSidebarVisible ? 'pointer-events-none opacity-0' : 'opacity-100 delay-150'
              }`}
              aria-label="Seitenleiste anzeigen"
              title="Seitenleiste anzeigen"
              tabIndex={chatSidebarVisible ? -1 : 0}
            >
              <PanelLeft className="h-4 w-4" />
            </button>
            {isSearchActive ? (
              <div className={`relative z-10 h-full min-h-0 ${chatSidebarVisible ? '' : 'md:pt-12'}`}>
                <ChatSearchView
                  onSelectChat={handleSelectChat}
                  recentChats={recentChatsList}
                  currentChatId={chatId}
                  onTogglePin={handleTogglePin}
                  onDeleteChat={handleDeleteChat}
                  pinPatch={pinPatch}
                />
              </div>
            ) : workspaceView === 'customize' || workspaceView === 'scheduled' ? (
              <div className={`relative z-10 h-full overflow-y-auto ${chatSidebarVisible ? '' : 'md:pt-12'}`}>
                {workspaceView === 'customize' ? <ChatCustomizeView /> : (
                  <div className="mx-auto w-full max-w-[700px] px-6 py-10">
                    <AssistantSettingsSection part="jobs" embedded />
                  </div>
                )}
              </div>
            ) : workspaceView ? (
              <div className={`relative z-10 h-full overflow-y-auto bg-app-surface ${chatSidebarVisible ? '' : 'md:pt-12'}`}>
                {workspaceView === 'projects' ? (
                  <ChatProjectsView activeProjectId={activeProjectId} onOpenProject={handleOpenProject} />
                ) : null}
              </div>
            ) : (
              <>
                <ChatHeader
                  title={chatId ? currentChatTitle : null}
                  menuEnabled={Boolean(chatId)}
                  isPinned={Boolean(currentChatPinnedAt)}
                  onTogglePin={
                    chatId
                      ? () => void handleTogglePin(chatId, currentChatPinnedAt == null, currentChatPinnedAt)
                      : undefined
                  }
                  onRename={handleHeaderRename}
                  onDelete={handleHeaderDelete}
                  leadingInset={!chatSidebarVisible}
                  extraItems={chatAccess === 'read' ? undefined : [
                    { label: 'Als Skill speichern', icon: <BookmarkPlus className="h-3.5 w-3.5 text-app-text-subtle" />, onClick: () => setSkillDraftOpen(true) },
                    { label: 'Chat teilen', icon: <Share2 className="h-3.5 w-3.5 text-app-text-subtle" />, onClick: () => setShareOpen(true) },
                  ]}
                />
                <div className="relative flex-1 min-h-0 overflow-hidden">
                  <AiChatPanel
                    key={chatKey}
                    chatId={panelChatId}
                    onChatCreated={handleChatCreated}
                    onTitleGenerated={handleTitleGenerated}
                    onChatMetadata={handleChatMetadata}
                    projectId={activeProjectId}
                  />
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      {skillDraftOpen && chatId ? (
        <SkillDraftDialog
          chatId={chatId}
          canEnable={canEnableSkill}
          onClose={() => setSkillDraftOpen(false)}
          onSaved={() => {
            setSkillDraftOpen(false);
            pushToast('Skill gespeichert.');
          }}
        />
      ) : null}
      {shareOpen && chatId ? (
        <ChatShareDialog
          chatId={chatId}
          initialVisibility={chatVisibility}
          initialGrantees={chatGrantees}
          onClose={() => setShareOpen(false)}
          onSaved={(visibility, granteeUserIds) => {
            setChatVisibility(visibility);
            setChatGrantees(granteeUserIds);
            setShareOpen(false);
            pushToast('Freigabe gespeichert.');
          }}
        />
      ) : null}
      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
